import { StoreService } from "src/app/services/apps/store/store.service";
import {
  ProviderCaseStatusGetDTO,
  ProviderClaimTypesGetDTO,
  ProviderGetDTO,
  ProviderStatusAppliesTo,
} from "src/app/pages/providers/provider.model";
import { filterProviderStatusesByAppliesTo } from "src/app/services/apps/providers/provider-status.helper";
import { StoreGetDTO } from "src/app/pages/store-management/stores/store";

/** Empty storeId means "All stores" for the selected provider. */
export const STORE_PROVIDER_ALL_STORES_VALUE = "";

/** Empty provider means "All providers" when allowAllProviders is enabled (e.g. dashboard). */
export const STORE_PROVIDER_ALL_PROVIDERS_VALUE = "";

/** @deprecated Use STORE_PROVIDER_ALL_STORES_VALUE */
export const REPORT_STORE_ALL_VALUE = STORE_PROVIDER_ALL_STORES_VALUE;

export interface StoreProviderFilterOptions {
  /** When true, empty provider selects all providers (dashboard only). */
  allowAllProviders?: boolean;
  /** When true, store/claim-type/status options stay empty until a provider is selected. */
  requireProviderForScopedFilters?: boolean;
}

export interface StoreProviderFilterState {
  providerOptions: ProviderGetDTO[];
  storesForProvider: StoreGetDTO[];
  showProviderFilter: boolean;
  showStoreFilter: boolean;
  provider: string;
  storeId: string;
}

export class StoreProviderFilterHelper {
  static isProviderSelected(provider?: string | null): boolean {
    return !!provider;
  }

  static getCompletedStores(stores: StoreGetDTO[]): StoreGetDTO[] {
    return StoreService.getCompletedStores(stores);
  }

  static getFilteredProviders(
    allProviders: ProviderGetDTO[],
    completedStores: StoreGetDTO[],
  ): ProviderGetDTO[] {
    const providerValues = new Set(
      completedStores.map((store) => store.provider),
    );
    return allProviders.filter((provider) =>
      providerValues.has(provider.value),
    );
  }

  static getStoresForProvider(
    completedStores: StoreGetDTO[],
    provider: string,
    options?: StoreProviderFilterOptions,
  ): StoreGetDTO[] {
    if (!provider) {
      return options?.requireProviderForScopedFilters ? [] : completedStores;
    }

    return completedStores.filter((store) => store.provider === provider);
  }

  static showProviderSelect(filteredProviders: ProviderGetDTO[]): boolean {
    return filteredProviders.length > 1;
  }

  static showStoreSelect(storesForProvider: StoreGetDTO[]): boolean {
    return storesForProvider.length > 1;
  }

  static showProviderFilter(filteredProviders: ProviderGetDTO[]): boolean {
    return filteredProviders.length > 0;
  }

  static showStoreFilter(storesForProvider: StoreGetDTO[]): boolean {
    return storesForProvider.length > 0;
  }

  /**
   * Resolves provider/store filter visibility and defaults for views like Dashboard.
   * - Provider control: visible when at least one provider; readonly when only one.
   * - Store control: visible when at least one store for the selected provider;
   *   readonly when only one.
   * - With `allowAllProviders`: empty provider/store mean all (dashboard filters).
   * - Values are still applied in the background when controls are readonly.
   */
  static resolveStoreProviderFilterState(
    allProviders: ProviderGetDTO[],
    stores: StoreGetDTO[],
    currentProvider = "",
    currentStoreId = "",
    options?: StoreProviderFilterOptions,
  ): StoreProviderFilterState {
    const completedStores = StoreProviderFilterHelper.getCompletedStores(stores);
    const providerOptions = StoreProviderFilterHelper.getFilteredProviders(
      allProviders,
      completedStores,
    );

    if (!providerOptions.length) {
      return {
        providerOptions: [],
        storesForProvider: [],
        showProviderFilter: false,
        showStoreFilter: false,
        provider: "",
        storeId: "",
      };
    }

    const defaults = StoreProviderFilterHelper.buildStoreProviderDefaults(
      completedStores,
      providerOptions,
      currentProvider,
      currentStoreId,
      options,
    );

    const storesForProvider = StoreProviderFilterHelper.getStoresForProvider(
      completedStores,
      defaults.provider,
    );

    return {
      providerOptions,
      storesForProvider,
      showProviderFilter:
        StoreProviderFilterHelper.showProviderFilter(providerOptions),
      showStoreFilter:
        StoreProviderFilterHelper.showStoreFilter(storesForProvider),
      provider: defaults.provider,
      storeId: defaults.storeId,
    };
  }

  static onlyOneProvider(filteredProviders: ProviderGetDTO[]): boolean {
    return filteredProviders.length === 1;
  }

  static onlyOneStoreForProvider(storesForProvider: StoreGetDTO[]): boolean {
    return storesForProvider.length === 1;
  }

  static resolveProviderDisplayName(
    filteredProviders: ProviderGetDTO[],
    selectedProvider?: string,
  ): string {
    const provider = selectedProvider
      ? filteredProviders.find((item) => item.value === selectedProvider)
      : filteredProviders[0];

    return provider?.name ?? "";
  }

  static resolveStoreDisplayName(
    storesForProvider: StoreGetDTO[],
    storeId?: string,
  ): string {
    if (storeId) {
      return (
        storesForProvider.find((store) => store.storeId === storeId)?.name ?? ""
      );
    }

    return storesForProvider[0]?.name ?? "";
  }

  static resolveDefaultProvider(
    filteredProviders: ProviderGetDTO[],
    queryProvider?: string,
    options?: StoreProviderFilterOptions,
  ): string {
    if (filteredProviders.length === 1) {
      return filteredProviders[0].value;
    }

    if (options?.allowAllProviders) {
      if (!queryProvider) {
        return STORE_PROVIDER_ALL_PROVIDERS_VALUE;
      }

      if (
        filteredProviders.some((provider) => provider.value === queryProvider)
      ) {
        return queryProvider;
      }

      return STORE_PROVIDER_ALL_PROVIDERS_VALUE;
    }

    if (
      queryProvider &&
      filteredProviders.some((provider) => provider.value === queryProvider)
    ) {
      return queryProvider;
    }

    return filteredProviders[0]?.value ?? "";
  }

  static resolveDefaultStoreId(
    storesForProvider: StoreGetDTO[],
    queryStoreId?: string,
  ): string {
    if (
      queryStoreId &&
      storesForProvider.some((store) => store.storeId === queryStoreId)
    ) {
      return queryStoreId;
    }

    if (storesForProvider.length === 1) {
      return storesForProvider[0].storeId;
    }

    return STORE_PROVIDER_ALL_STORES_VALUE;
  }

  static buildStoreProviderDefaults(
    completedStores: StoreGetDTO[],
    filteredProviders: ProviderGetDTO[],
    currentProvider: string,
    currentStoreId: string,
    options?: StoreProviderFilterOptions,
  ): { provider: string; storeId: string } {
    const provider = StoreProviderFilterHelper.resolveDefaultProvider(
      filteredProviders,
      currentProvider,
      options,
    );
    const storesForProvider = StoreProviderFilterHelper.getStoresForProvider(
      completedStores,
      provider,
      options,
    );

    let storeId = currentStoreId;
    const storeIsValid =
      storeId === STORE_PROVIDER_ALL_STORES_VALUE ||
      storesForProvider.some((store) => store.storeId === storeId);

    if (!storeIsValid) {
      storeId =
        StoreProviderFilterHelper.resolveDefaultStoreId(storesForProvider);
    }

    if (
      !storeId &&
      storesForProvider.length === 1 &&
      !options?.allowAllProviders
    ) {
      storeId = storesForProvider[0].storeId;
    }

    return { provider, storeId };
  }

  static buildInitialStoreProviderParams(
    stores: StoreGetDTO[],
    allProviders: ProviderGetDTO[],
    queryStoreId?: string,
    queryProvider?: string,
    options?: StoreProviderFilterOptions,
  ): { storeId: string; provider: string } {
    const completedStores = StoreProviderFilterHelper.getCompletedStores(stores);
    const filteredProviders = StoreProviderFilterHelper.getFilteredProviders(
      allProviders,
      completedStores,
    );

    let provider = StoreProviderFilterHelper.resolveDefaultProvider(
      filteredProviders,
      queryProvider,
      options,
    );

    if (queryStoreId) {
      const store = completedStores.find(
        (item) => item.storeId === queryStoreId,
      );
      if (store && filteredProviders.some((p) => p.value === store.provider)) {
        provider = store.provider;
      }
    }

    const storesForProvider = StoreProviderFilterHelper.getStoresForProvider(
      completedStores,
      provider,
      options,
    );
    const storeId = StoreProviderFilterHelper.resolveDefaultStoreId(
      storesForProvider,
      queryStoreId,
    );

    return { provider, storeId };
  }

  static filterClaimTypesByProvider(
    claimTypes: ProviderClaimTypesGetDTO[],
    provider?: string,
    options?: StoreProviderFilterOptions,
  ): ProviderClaimTypesGetDTO[] {
    if (!provider) {
      return options?.requireProviderForScopedFilters ? [] : claimTypes;
    }

    return claimTypes.filter((type) => type.provider === provider);
  }

  static filterStatusesByProvider(
    statuses: ProviderCaseStatusGetDTO[],
    provider?: string,
    appliesTo?: ProviderStatusAppliesTo,
    options?: StoreProviderFilterOptions,
  ): ProviderCaseStatusGetDTO[] {
    let filtered = appliesTo
      ? filterProviderStatusesByAppliesTo(statuses, appliesTo)
      : statuses;

    if (!provider) {
      return options?.requireProviderForScopedFilters ? [] : filtered;
    }

    return filtered.filter((status) => status.provider === provider);
  }

  /**
   * Resolves which provider scopes claim-type / status filter options.
   * - With a provider filter control: use the selected provider.
   * - Store-only filter: use the provider of the selected store; all options when store is "All".
   */
  static resolveProviderForScopedFilters(
    hasProviderFilter: boolean,
    selectedProvider: string | undefined,
    selectedStoreId: string | undefined,
    stores: StoreGetDTO[],
  ): string | undefined {
    if (hasProviderFilter) {
      return selectedProvider || undefined;
    }

    if (!selectedStoreId) {
      return undefined;
    }

    return stores.find((store) => store.storeId === selectedStoreId)?.provider;
  }

  static mergeStoreProviderParams(
    source: Record<string, unknown>,
    completedStores: StoreGetDTO[],
    filteredProviders: ProviderGetDTO[],
    options?: StoreProviderFilterOptions,
  ): Record<string, unknown> {
    const defaults = StoreProviderFilterHelper.buildStoreProviderDefaults(
      completedStores,
      filteredProviders,
      String(source["provider"] ?? ""),
      String(source["storeId"] ?? ""),
      options,
    );
    const storesForProvider = StoreProviderFilterHelper.getStoresForProvider(
      completedStores,
      defaults.provider,
      options,
    );
    const showStore =
      StoreProviderFilterHelper.showStoreSelect(storesForProvider);

    const merged: Record<string, unknown> = { ...source };

    if (defaults.provider) {
      merged["provider"] = defaults.provider;
    } else {
      delete merged["provider"];
    }

    if (showStore) {
      if (defaults.storeId) {
        merged["storeId"] = defaults.storeId;
      } else {
        delete merged["storeId"];
      }
    } else if (storesForProvider.length === 1) {
      merged["storeId"] = storesForProvider[0].storeId;
    } else {
      delete merged["storeId"];
    }

    return Object.fromEntries(
      Object.entries(merged).filter(
        ([, value]) => value !== null && value !== "" && value !== undefined,
      ),
    );
  }

  static buildStoreProviderApiParams(
    storeId: string,
    provider: string,
    stores: StoreGetDTO[],
    allProviders: ProviderGetDTO[],
    options?: StoreProviderFilterOptions,
  ): { provider: string; storeId?: string } {
    const completedStores = StoreProviderFilterHelper.getCompletedStores(stores);
    const filteredProviders = StoreProviderFilterHelper.getFilteredProviders(
      allProviders,
      completedStores,
    );
    const merged = StoreProviderFilterHelper.mergeStoreProviderParams(
      { storeId, provider },
      completedStores,
      filteredProviders,
      options,
    );

    const result: { provider: string; storeId?: string } = {
      provider: String(merged["provider"] ?? ""),
    };

    if (merged["storeId"]) {
      result.storeId = String(merged["storeId"]);
    }

    return result;
  }
}

/** @deprecated Use StoreProviderFilterHelper */
export const ReportStoreFilterHelper = StoreProviderFilterHelper;
